// Business briefings: the pure bits shared by the front-page card and the
// Briefings page (labels, dates, the numbers panel, the context nudge).
// The briefings themselves are written on the server (garageFarmServer
// helpers/briefing.js), twice a day by default.

const TZ = 'Europe/London';

export const KIND_LABEL = { morning: 'Morning briefing', evening: 'Evening briefing', manual: 'Test briefing' };

export const MODELS = ['qwen3.6:35b-a3b', 'gpt-oss:20b', 'gemma4:e4b-it-qat'];

export const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

// 'Fri 30 Oct, 06:31'
export function formatWhen(date) {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-GB', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short' }).replace(/,/g, '');
  const time = d.toLocaleTimeString('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return `${day}, ${time}`;
}

export const briefingTitle = (b) => KIND_LABEL[b?.kind] || 'Briefing';

// The written briefing as paragraphs (blank lines between them).
export const paragraphs = (text) => String(text || '').split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);

// Older than this, the front page says no briefing has come in lately.
export const STALE_HOURS = 14;
export const isStale = (b, now = Date.now()) => !b || now - new Date(b.startedAt).getTime() > STALE_HOURS * 3.6e6;

// Context sections still holding the seed's [placeholders].
export function unfilled(context, sections) {
  return (sections || []).filter(s => /\[[^\]\n]{2,}\]/.test(context?.[s.key] || '')).map(s => s.title);
}

// Times as the server keeps them: valid, unique, sorted.
export function cleanTimes(times) {
  return [...new Set((times || []).map(t => String(t).trim()).filter(t => TIME_RE.test(t)))].sort();
}

// ── The numbers panel ─────────────────────────────────────────────────────────
// The snapshot a briefing was written from, as readable rows. Its keys are
// camelCase English ("ordersRoundPassedNotMarkedDelivered"), so they're
// turned back into words rather than labelled one by one.

const SECTION_TITLES = {
  period: 'Period', sales: 'Sales', deliveries: 'Deliveries', subscriptions: 'Subscriptions', trade: 'Trade',
  stock: 'Stock', leaflets: 'Leaflets', website: 'Website', farm: 'Farm sensors', farmCommander: 'Farm Commander',
  trays: 'Trays and harvests', ops: 'server2', weather: 'Weather', missing: 'Missing data',
};

const WORDS = { pct: '%', c: '°C', mph: 'mph', g: 'g', ppm: 'ppm', ec: 'EC', ph: 'pH', co2: 'CO2', ac: 'AC', qr: 'QR' };

export function humanise(key) {
  if (WORDS[String(key).toLowerCase()]) return WORDS[String(key).toLowerCase()];
  const words = String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([a-z])([0-9])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[\s_]+/)
    .filter(Boolean)
    .map(w => WORDS[w.toLowerCase()] || w.toLowerCase());
  const text = words.join(' ').replace(/ (%|°C)/g, ' ($1)');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const scalar = (v) => {
  if (v === null || v === undefined) return 'n/a';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return String(v);
};

// 'rain chance 0%', 'temp 13.9°C': a unit in the label moves onto the value
function pair(key, value) {
  const label = humanise(key);
  const unit = label.match(/ \((%|°C)\)$/);
  const words = unit ? label.slice(0, -unit[0].length) : label;
  const lead = /^[A-Z][a-z]/.test(words) ? words.charAt(0).toLowerCase() + words.slice(1) : words;
  return `${lead} ${Array.isArray(value) ? value.join('/') : scalar(value)}${unit ? unit[1] : ''}`;
}

// One item of a list, on one line. A leading name ('Today late morning',
// 'Hockley Woods') heads it.
function describeItem(item) {
  const entries = Object.entries(item || {});
  if (entries.length > 1 && typeof entries[0][1] === 'string') {
    const [[, name], ...rest] = entries;
    return `${name}: ${rest.map(([k, v]) => pair(k, v)).join(', ')}`;
  }
  return entries.map(([k, v]) => pair(k, v)).join(', ');
}

function rowsFor(value, depth = 0, label = null) {
  if (value === null || typeof value !== 'object') return [{ label, value: scalar(value), depth }];
  if (Array.isArray(value)) {
    if (!value.length) return [{ label, value: 'none', depth }];
    if (value.every(v => v === null || typeof v !== 'object')) return [{ label, value: value.map(scalar).join(', '), depth }];
    return [
      ...(label ? [{ label, value: '', depth }] : []),
      ...value.map(v => ({ label: null, value: describeItem(v), depth: depth + (label ? 1 : 0) })),
    ];
  }
  const entries = Object.entries(value);
  if (!entries.length) return [{ label, value: 'none', depth }];
  return [
    ...(label ? [{ label, value: '', depth }] : []),
    ...entries.flatMap(([k, v]) => rowsFor(v, depth + (label ? 1 : 0), humanise(k))),
  ];
}

// [{ key, title, rows: [{ label, value, depth }] }]
export function snapshotSections(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return [];
  return Object.entries(snapshot)
    .filter(([key, v]) => !(key === 'missing' && Array.isArray(v) && !v.length))
    .map(([key, v]) => ({
      key,
      title: SECTION_TITLES[key] || humanise(key),
      rows: Array.isArray(v) ? v.map(x => ({ label: null, value: scalar(x), depth: 0 })) : rowsFor(v),
    }));
}
